import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  type LoaderFunctionArgs,
  useLoaderData,
  useOutletContext,
  useRevalidator,
} from "react-router";

import AssignmentApi from "@/api/assignment-api";
import AssignmentUsers from "@/components/assignment-users";
import ApiErrorComponent from "@/components/common/api-error";
import Button from "@/components/common/input/button";
import Input from "@/components/common/input/input";
import Select from "@/components/common/input/select";
import Modal from "@/components/common/modal";
import { handled } from "@/helpers";
import { usePageTitle } from "@/hooks/title";
import type ApiError from "@/models/ApiError";
import type { Assignment, AssignmentUser } from "@/types/assignment";

type LoaderData = {
  assignment_users: AssignmentUser[] | null;
  error: ApiError | null;
};

export async function loader({
  params: { assignment_id },
}: LoaderFunctionArgs): Promise<LoaderData> {
  if (!assignment_id) {
    throw new Error("Missing assignment_id");
  }
  const { error, data } = await AssignmentApi.getAssignmentUsers(assignment_id);
  return { error, assignment_users: data };
}

export default function AssignmentUserAdmin() {
  const [assignment] = useOutletContext<[Assignment]>();
  const { assignment_users, error } = useLoaderData() as LoaderData;
  const revalidator = useRevalidator();
  usePageTitle("Users");

  if (error) {
    return <ApiErrorComponent error={error} />;
  }

  function _callbackFn() {
    void revalidator.revalidate();
  }

  return (
    <div className="mx-auto max-w-[60rem]">
      <Modal>
        <Button>Add user</Button>
        <AddUserModal assignment_id={assignment.id} callbackFn={_callbackFn} />
      </Modal>
      <AssignmentUsers assignment_users={assignment_users ?? []} />
    </div>
  );
}

interface AddUserModalValues {
  email: string;
  role: string;
}

// toggleFn arrives from the surrounding Modal through cloneElement, never from the call site.
interface AddUserModalProps {
  assignment_id: string;
  callbackFn: () => void;
  toggleFn?: () => void;
}

const AddUserModal = ({ assignment_id, callbackFn, toggleFn }: AddUserModalProps) => {
  const [error, setError] = useState<ApiError | null>(null);
  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<AddUserModalValues>();

  async function _handleSubmit(form_data: AddUserModalValues) {
    clearErrors();
    setError(null);
    const { error: api_error } = await AssignmentApi.addAssignmentUser(
      form_data.email,
      assignment_id,
      form_data.role,
    );
    if (api_error) {
      console.error(api_error);
      setError(api_error);
    } else {
      toggleFn?.();
      callbackFn();
    }
  }

  const _handleCancel = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    toggleFn?.();
  };

  return (
    <div className="mx-auto mt-4 w-[32rem] rounded-md bg-slate-800 p-8">
      <form onSubmit={handled(handleSubmit(_handleSubmit))}>
        <Input
          label="Email"
          id="email"
          type="email"
          error={errors.email?.message}
          register={register}
          required
        />
        <Select error={errors.role?.message} label={"Role"} id="role" register={register} required>
          <option value={"assessor"}>Assessor</option>
          <option value={"full_access"}>Full access</option>
        </Select>
        <div className="flex justify-end gap-4">
          <Button onClick={_handleCancel}>Cancel</Button>
          <Button type="submit">Submit</Button>
        </div>
      </form>
      {error && <ApiErrorComponent error={error} />}
    </div>
  );
};
